import ExclamationTriangleIcon from "@heroicons/react/20/solid/ExclamationTriangleIcon";
import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";
import React, { FC, ReactNode, useMemo } from "react";
import { Node } from "reactflow";
import { useAppSelector } from "store/hooks";
import type {
  WorkflowPreflightIssue,
  WorkflowPreflightIssueSeverity,
} from "reducers/flow-builder.reducer";
import type { NodeData } from "./Nodes/NodeData";

const NODE_TYPE_LABELS: Record<string, string> = {
  start: "Start",
  empty: "Empty",
  message: "Message",
  jumpTo: "Jump to",
  exit: "Exit",
  waitUntil: "Wait until",
  timeDelay: "Time delay",
  push: "Push",
  timeWindow: "Time window",
  userAttribute: "User attribute",
  insertNode: "Insert",
  tracker: "Custom component",
  multisplit: "Multisplit",
  experiment: "Experiment",
  aiResponse: "AI response",
  responseBranch: "Response branch",
  ricochet: "Ricochet",
};

const sameValue = (left?: string, right?: string) =>
  left !== undefined &&
  left !== null &&
  right !== undefined &&
  right !== null &&
  String(left) === String(right);

const capitalizeText = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const isBlockingSeverity = (severity?: WorkflowPreflightIssueSeverity) =>
  severity !== "warning" && severity !== "info";

export const getPreflightIssuesForNode = (
  issues: WorkflowPreflightIssue[],
  nodeId: string,
  stepId?: string
) =>
  issues.filter(
    (issue) =>
      sameValue(issue.nodeId, nodeId) ||
      (!!stepId && sameValue(issue.stepId, stepId))
  );

export const findNodeForPreflightIssue = (
  issue: WorkflowPreflightIssue,
  nodes: Node<NodeData>[]
) =>
  nodes.find((node) => sameValue(issue.nodeId, node.id)) ||
  nodes.find((node) => sameValue(issue.stepId, node.data?.stepId));

export const findFirstNodeForPreflightIssues = (
  issues: WorkflowPreflightIssue[],
  nodes: Node<NodeData>[]
) => {
  for (const issue of issues) {
    const node = findNodeForPreflightIssue(issue, nodes);
    if (node) return node;
  }
  return undefined;
};

export const getPreflightNodeLabel = (node?: Node<NodeData>) => {
  if (!node) return "Workflow";

  const data = node.data as any;
  if (data?.customName) return data.customName;

  if (
    (node.type === "message" || node.type === "push") &&
    data?.template?.type
  ) {
    return `${capitalizeText(data.template.type)} message`;
  }

  return (
    NODE_TYPE_LABELS[node.type || ""] ||
    NODE_TYPE_LABELS[data?.type || ""] ||
    "Workflow step"
  );
};

const issueText = (issue: WorkflowPreflightIssue) =>
  issue.message || issue.code || "Validation issue";

const issueMetaText = (issue: WorkflowPreflightIssue) =>
  [issue.field, issue.code].filter(Boolean).join(" - ");

const issueKey = (issue: WorkflowPreflightIssue, index: number) =>
  [issue.nodeId, issue.stepId, issue.code, issue.field, index]
    .filter(Boolean)
    .join("-");

export const NodePreflightFrame: FC<{
  nodeId: string;
  stepId?: string;
  children: ReactNode;
}> = ({ nodeId, stepId, children }) => {
  const issues = useAppSelector(
    (state) => state.flowBuilder.activationPreflight.issues
  );
  const nodeIssues = useMemo(
    () => getPreflightIssuesForNode(issues, nodeId, stepId),
    [issues, nodeId, stepId]
  );

  if (nodeIssues.length === 0) return <>{children}</>;

  const hasBlockingIssue = nodeIssues.some((issue) =>
    isBlockingSeverity(issue.severity)
  );
  const borderColor = hasBlockingIssue
    ? "border-[#F43F5E]"
    : "border-[#F59E0B]";
  const badgeColor = hasBlockingIssue ? "bg-[#DC2626]" : "bg-[#D97706]";

  return (
    <div className="relative inline-block">
      {children}
      <div
        className={`pointer-events-none absolute inset-[-6px] z-[-1] rounded-[8px] border-2 ${borderColor} shadow-[0_0_0_4px_rgba(244,63,94,0.18)]`}
      />
      <div
        className={`pointer-events-none absolute right-[-14px] top-[-14px] z-10 flex min-h-[24px] min-w-[24px] items-center justify-center gap-[2px] rounded-full px-[6px] text-[11px] font-semibold leading-none text-white shadow ${badgeColor}`}
      >
        <ExclamationTriangleIcon className="h-[13px] w-[13px]" />
        {nodeIssues.length}
      </div>
    </div>
  );
};

export const PreflightIssuesBanner: FC<{
  issues: WorkflowPreflightIssue[];
  issueCount?: number;
  nodes: Node<NodeData>[];
  onSelectNode: (nodeId: string) => void;
  onDismiss: () => void;
}> = ({ issues, issueCount, nodes, onSelectNode, onDismiss }) => {
  const visibleIssues = issues.slice(0, 5);
  const count = Math.max(issueCount || 0, issues.length);
  const remainingCount = Math.max(0, count - visibleIssues.length);

  if (issues.length === 0) return null;

  return (
    <div className="z-10 mx-4 mt-3 rounded border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-[#7F1D1D] shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-inter text-[14px] font-semibold leading-[22px]">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-[#DC2626]" />
            <span>Workflow validation failed</span>
          </div>
          <div className="mt-1 text-[13px] leading-5 text-[#991B1B]">
            {count} {count === 1 ? "issue needs" : "issues need"} attention
            before this workflow can start.
          </div>
        </div>
        <button
          type="button"
          aria-label="Dismiss validation issues"
          className="rounded p-1 text-[#991B1B] hover:bg-[#FEE2E2]"
          onClick={onDismiss}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 flex flex-col gap-[6px]">
        {visibleIssues.map((issue, index) => {
          const node = findNodeForPreflightIssue(issue, nodes);
          const content = (
            <>
              <div className="font-medium text-[#7F1D1D]">
                {getPreflightNodeLabel(node)}
              </div>
              <div className="text-[#991B1B]">{issueText(issue)}</div>
              {issueMetaText(issue) && (
                <div className="text-[12px] leading-5 text-[#B91C1C]">
                  {issueMetaText(issue)}
                </div>
              )}
            </>
          );

          if (!node) {
            return (
              <div
                key={issueKey(issue, index)}
                className="rounded border border-[#FECACA] bg-white/70 px-3 py-2 text-left text-[13px] leading-5"
              >
                {content}
              </div>
            );
          }

          return (
            <button
              type="button"
              key={issueKey(issue, index)}
              className="rounded border border-[#FECACA] bg-white/70 px-3 py-2 text-left text-[13px] leading-5 hover:border-[#F87171] hover:bg-white"
              onClick={() => onSelectNode(node.id)}
            >
              {content}
            </button>
          );
        })}
        {remainingCount > 0 && (
          <div className="text-[12px] leading-5 text-[#991B1B]">
            {remainingCount} more {remainingCount === 1 ? "issue" : "issues"}
          </div>
        )}
      </div>
    </div>
  );
};

export const PreflightIssuesPanel: FC<{
  issues: WorkflowPreflightIssue[];
}> = ({ issues }) => {
  if (issues.length === 0) return null;

  return (
    <div className="mt-3 rounded border border-[#FCA5A5] bg-[#FEF2F2] p-3 text-[#7F1D1D]">
      <div className="flex items-center gap-2 font-inter text-[13px] font-semibold leading-5">
        <ExclamationTriangleIcon className="h-4 w-4 text-[#DC2626]" />
        <span>Validation</span>
      </div>
      <div className="mt-2 flex flex-col gap-2">
        {issues.map((issue, index) => (
          <div key={issueKey(issue, index)} className="text-[13px] leading-5">
            <div>{issueText(issue)}</div>
            {issueMetaText(issue) && (
              <div className="text-[12px] text-[#B91C1C]">
                {issueMetaText(issue)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
